import { cn } from "@/shared/lib/cn";
import { IconButton } from "./IconButton";
import { Spinner } from "./Spinner";

/** Voice-dictation wiring, owned by the feature (the shared/lib `useVoiceInput`). */
export interface PromptVoiceProps {
  supported: boolean;
  status: "idle" | "recording" | "transcribing";
  onToggle: () => void;
  startLabel: string;
  stopLabel: string;
}

export interface PromptToolsProps {
  /** Run the "enhance with AI" action on the current text. */
  onEnhance: () => void;
  enhancing: boolean;
  enhanceLabel: string;
  /** Disables enhance (e.g. the field is empty). Dictation stays available. */
  disabled?: boolean;
  /** When present, renders a dictation (Whisper) mic button. */
  voice?: PromptVoiceProps;
  className?: string;
}

/**
 * A compact actions row for any prompt/instructions field: "Enhance with AI"
 * (helper-model rewrite) plus optional voice dictation. Presentational — the
 * feature owns the enhance mutation and the `useVoiceInput` hook and passes the
 * already-translated labels, matching the rest of `shared/ui`.
 */
export function PromptTools({
  onEnhance,
  enhancing,
  enhanceLabel,
  disabled,
  voice,
  className,
}: PromptToolsProps) {
  const recording = voice?.status === "recording";
  const transcribing = voice?.status === "transcribing";

  return (
    <div className={cn("flex items-center gap-1", className)}>
      <button
        type="button"
        onClick={onEnhance}
        disabled={disabled || enhancing}
        aria-label={enhanceLabel}
        className="inline-flex h-7 items-center gap-1.5 rounded-pill bg-accent px-2.5 font-sans text-[12px] font-medium text-white outline-none transition-opacity duration-150 ease-house hover:opacity-90 focus-visible:opacity-90 disabled:pointer-events-none disabled:opacity-50"
      >
        {enhancing ? <Spinner size={12} /> : <SparkIcon className="size-3.5" />}
        {enhanceLabel}
      </button>

      {voice && voice.supported ? (
        <IconButton
          size="sm"
          onClick={voice.onToggle}
          disabled={transcribing}
          aria-label={recording ? voice.stopLabel : voice.startLabel}
          className={cn(
            "size-7 rounded-pill",
            recording && "animate-pulse bg-ink text-paper hover:bg-ink",
          )}
        >
          {transcribing ? <Spinner size={13} /> : <MicIcon className="size-4" />}
        </IconButton>
      ) : null}
    </div>
  );
}

function SparkIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M12 3v4M12 17v4M3 12h4M17 12h4" />
      <path d="m6.3 6.3 2.1 2.1M15.6 15.6l2.1 2.1M17.7 6.3l-2.1 2.1M8.4 15.6l-2.1 2.1" />
    </svg>
  );
}

function MicIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
    </svg>
  );
}
